import { observer } from 'mobx-react-lite'
import { Copy, Page, Trash } from 'iconoir-react'
import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { FORM_CATEGORY_LABELS, parseQuestions } from 'app/lib/forms/builder'
import type { FormCategory } from 'app/lib/forms/types'
import { closeModal, showModal } from 'app/lib/modal'
import { FORM_TEMPLATES } from 'app/lib/forms/formTemplates'
import type { FormListEntry, UseFormsListReturn } from './useFormsList'
import { FORMS_SECTION_TABS } from './formsSectionTabs'
import { FormStatusBadge } from './FormStatusBadge'

function StarterChooser({ onChoose }: { onChoose: UseFormsListReturn['createForm'] }) {
  const choose = (starter?: (typeof FORM_TEMPLATES)[number]) => {
    closeModal()
    onChoose(starter)
  }
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-strong">New form</h2>
        <p className="text-sm text-subtle">Start from a template and change anything, or start from nothing.</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {FORM_TEMPLATES.map((starter) => (
          <Button key={starter.key} variant="card" onClick={() => choose(starter)}>
            <span className="flex flex-col gap-1">
              <span className="font-medium">{starter.name}</span>
              <span className="text-xs text-subtle">{`${starter.questions.length} questions`}</span>
            </span>
          </Button>
        ))}
        <Button variant="dashed" className="h-auto min-h-16" onClick={() => choose()}>
          Blank form
        </Button>
      </div>
    </div>
  )
}

export const FormsListPage = observer(function FormsListPage({
  forms,
  creating,
  createForm,
  openForm,
  deleteForm,
  duplicateForm,
}: UseFormsListReturn) {
  const openChooser = () =>
    showModal(<StarterChooser onChoose={createForm} />, {
      useRadix: true,
      isFullScreen: false,
      hideTitle: true,
      removePadding: true,
      panelClassName: `min-w-0 sm:max-w-xl`,
    })

  const columns = useMemo<ColumnDef<FormListEntry>[]>(
    () => [
      {
        id: `name`,
        accessorFn: (entry) => entry.latest.name,
        header: `Name`,
        cell: (props) => <span className="font-medium">{props.getValue<string>()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `category`,
        accessorFn: (entry) => FORM_CATEGORY_LABELS[entry.latest.category as FormCategory] ?? entry.latest.category,
        header: `Category`,
        meta: { defaultVisible: true },
      },
      {
        id: `questions`,
        accessorFn: (entry) => parseQuestions(entry.latest.questionsJson).length,
        header: `Questions`,
        meta: { defaultVisible: true },
      },
      {
        id: `status`,
        header: `Status`,
        cell: (props) => <FormStatusBadge entry={props.row.original} />,
        meta: { defaultVisible: true },
      },
      {
        id: `updated`,
        accessorFn: (entry) => entry.latest.updatedDate,
        header: `Updated`,
        cell: (props) => moment(props.getValue<string>()).format(`MMM D, YYYY`),
        meta: { defaultVisible: true },
      },
      {
        id: `actions`,
        header: ``,
        meta: { defaultVisible: true },
        cell: (props) => (
          <div className="flex items-center gap-1">
            <TooltipWrapper message="Duplicate form">
              <Button
                variant="icon"
                size="icon-sm"
                aria-label="Duplicate form"
                onClick={(e) => {
                  e.stopPropagation()
                  duplicateForm(props.row.original)
                }}
              >
                <Copy className="text-subtle" />
              </Button>
            </TooltipWrapper>
            {/* A published version may already be pinned in a packet or a sent link, so only a
                form that never went live can be deleted. */}
            {!props.row.original.published && (
              <TooltipWrapper message="Delete form">
                <Button
                  variant="icon"
                  size="icon-sm"
                  aria-label="Delete form"
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteForm(props.row.original)
                  }}
                >
                  <Trash className="text-red-500" />
                </Button>
              </TooltipWrapper>
            )}
          </div>
        ),
      },
    ],
    [deleteForm, duplicateForm],
  )

  const newButton = (
    <Button variant="primary" size="sm" disabled={creating} onClick={openChooser}>
      New form
    </Button>
  )

  return (
    <PageScroll variant="table" header={<PageHeader title="Forms" tabs={FORMS_SECTION_TABS} />}>
      <div className="h-full pt-6">
        <SimpleTable
          columns={columns}
          data={forms}
          hideFooter
          meta={{ onRowClick: openForm }}
          emptyState={
            <TableEmptyState
              icon={<Page className="h-6 w-6" strokeWidth={1.5} />}
              title="No forms yet"
              description="Build the paperwork a new patient fills in before their first visit: registration, medical history, and consent. Start from a template and make it yours."
              action={newButton}
            />
          }
          edgePadded
          extraSlot={newButton}
        />
      </div>
    </PageScroll>
  )
})
